// عميل الـAPI: توكن الوصول في الذاكرة فقط، وتوكن التجديد في التخزين المحلي لاستعادة الجلسة بعد تحديث الصفحة.
// كل الصلاحيات تُفرض على الخادم — الواجهة لا تُخفي البيانات بل لا تطلب ما ليس من حقها.
import { isNativeApp, saveFileNative } from './platform.ts';

// الويب: نفس الأصل عبر Caddy. تطبيق أندرويد: عنوان مطلق يُحقن وقت البناء (`--mode android`).
const BASE = (import.meta.env?.VITE_API_URL as string | undefined) ?? '/api';
const REFRESH_KEY = 'icontravel.refresh';

export class ApiError extends Error {
  status: number; code: string; details?: unknown;
  constructor(status: number, code: string, message: string, details?: unknown) {
    super(message); this.status = status; this.code = code; this.details = details;
  }
}

let accessToken: string | null = null;

/**
 * الجلسة ضاعت فعلًا: كان معنا رمز تجديد ورفضه الخادم (انتهى أو أُلغي). بلا هذا الإخطار كانت
 * الواجهة تبقى على شكل «مسجَّل دخوله» بينما كل طلب يفشل بـ401 — فلا يعرف المستخدم أن عليه
 * الدخول من جديد. المشترك الوحيد هو مزوّد الجلسة، وهو من يعيد شاشة الدخول.
 */
type SessionLostListener = () => void;
const sessionLostListeners = new Set<SessionLostListener>();
export function onSessionLost(fn: SessionLostListener): () => void {
  sessionLostListeners.add(fn);
  return () => { sessionLostListeners.delete(fn); };
}
function notifySessionLost(): void {
  for (const fn of sessionLostListeners) { try { fn(); } catch { /* مستمع معطوب لا يوقف غيره */ } }
}
export const getRefreshToken = (): string | null => { try { return localStorage.getItem(REFRESH_KEY); } catch { return null; } };
export function setSession(access: string | null, refresh?: string | null): void {
  accessToken = access;
  try {
    if (refresh === null) localStorage.removeItem(REFRESH_KEY);
    else if (refresh) localStorage.setItem(REFRESH_KEY, refresh);
  } catch { /* وضع التصفح الخاص: الجلسة تبقى في الذاكرة فقط */ }
}
export const hasSession = (): boolean => accessToken !== null;

/**
 * **خروجٌ في تبويبٍ = خروجٌ في كلها** (مراجعة 23/09/2026): تبويبٌ خرج (بيده أو بالسكون) يمسح رمز
 * التجديد المشترك؛ فتسمعه البقيّة هنا وتعود إلى شاشة الدخول فورًا بدل أن تبقى قائمةً وكل طلبٍ
 * فيها يفشل. ومن دوّر الرمز فقط (لم يخرج) لا يُسمع هنا شيء يُخرج أحدًا.
 */
if (typeof window !== 'undefined') {
  window.addEventListener('storage', e => {
    if (e.key === REFRESH_KEY && e.newValue === null && accessToken) { accessToken = null; notifySessionLost(); }
  });
}

async function parse(res: Response): Promise<unknown> {
  if (res.status === 204) return null;
  const text = await res.text();
  if (!text) return null;
  try { return JSON.parse(text); } catch { return text; }
}

const isForm = (b: unknown): b is FormData => typeof FormData !== 'undefined' && b instanceof FormData;

/**
 * انحراف ساعة الجهاز عن ساعة الخادم بالميلي ثانية.
 *
 * النظام كلّه يؤرّخ بساعة **الخادم** بتوقيت القاهرة (الحضور، والحجوزات، والدفعات). فلو
 * عرضت الترويسة ساعة الجهاز وكانت متأخّرة ساعةً، لقرأ الموظف وقتًا يخالف ما يُسجَّل عنه.
 * والانحراف يُقاس من ترويسة `Date` التي تأتي مع **كل** ردّ — بلا مسار جديد، وبلا طلب
 * إضافي، ويُصحَّح نفسه مع كل نداء. ودقّتها ثانية واحدة زائد زمن الشبكة، وهي كافية لساعة
 * حائط؛ وما يحتاج دقّة أعلى من ذلك يُحسب على الخادم أصلًا كما هو اليوم.
 */
let serverSkewMs = 0;
export const serverNow = (): Date => new Date(Date.now() + serverSkewMs);

function noteServerTime(res: Response): void {
  const raw = res.headers?.get?.('date');
  if (!raw) return;                                   // وسيط لا يمرّرها، أو ردّ مُحاكى
  const t = Date.parse(raw);
  if (Number.isFinite(t)) serverSkewMs = t - Date.now();
}

/** آخر حركة تكتبها `useIdleLogout` في التخزين المشترك بين التبويبات — ولا حركة مسجَّلة ⇐ لا ترويسة */
export const LAST_ACTIVE_KEY = 'icontravel.lastActive';
function idleSeconds(): number | null {
  try {
    const t = Number(localStorage.getItem(LAST_ACTIVE_KEY));
    return Number.isFinite(t) && t > 0 ? Math.max(0, Math.round((Date.now() - t) / 1000)) : null;
  } catch { return null; }
}

async function send(method: string, path: string, body?: unknown, extra?: Record<string, string>): Promise<Response> {
  const headers: Record<string, string> = { ...extra };
  // multipart: يضبط المتصفح الـboundary بنفسه — لا نضع content-type يدويًا
  if (body !== undefined && !isForm(body)) headers['content-type'] = 'application/json';
  if (accessToken) headers.authorization = `Bearer ${accessToken}`;
  /**
   * كم ثانية مرّت على آخر حركة للمستخدم في أيّ تبويب (0155، قرار المالك 01/10/2026: «طلبات الموقع ماتروحش لحد مالوش أي
   * حركة جوّا البرنامج من 30 دقيقة»): الخادم يحسب منها «آخر نشاط» — فالاستطلاع في الخلفية لا يُحسب حركة.
   */
  if (accessToken) {
    const idle = idleSeconds();
    if (idle !== null) headers['x-idle-seconds'] = String(idle);
  }
  const payload = body === undefined ? undefined : isForm(body) ? body : JSON.stringify(body);
  const res = await fetch(`${BASE}${path}`, { method, headers, body: payload as BodyInit | undefined });
  noteServerTime(res);
  return res;
}

/**
 * تجديد الجلسة — **نداء واحد في كل لحظة مهما تعدّد الطالبون**.
 *
 * رمز التجديد يدور: كل استعمال ناجح يُبطله ويُصدر غيره. فلو انطلق نداءان بنفس الرمز — وهذا
 * يحدث فعلًا: عدة طلبات متوازية تعود 401 معًا، أو تبويبان، أو تركيب React في وضع التطوير —
 * لنجح الأول وفشل الثاني بـ401، فيُسقط الثاني الجلسة كلها ويُلقى بالمستخدم إلى شاشة الدخول
 * وهو في منتصف عمله. لذلك يتشارك الجميع وعدًا واحدًا: من وجده جاريًا انتظر نتيجته ولم يبدأ
 * دورةً ثانية تُبطل الأولى.
 */
let refreshing: Promise<boolean> | null = null;

export function refreshSession(): Promise<boolean> {
  if (refreshing) return refreshing;
  refreshing = acrossTabs(refreshOnce).finally(() => { refreshing = null; });
  return refreshing;
}

/**
 * **وعبر التبويبات أيضًا** (مراجعة 23/09/2026): الوعدُ المشترك يوحّد الطالبين في تبويبٍ واحد،
 * أمّا تبويبان فيقرآن نفس الرمز من التخزين ويدوّرانه معًا، فيراه الخادم «إعادة استخدام» ويُخرج
 * الاثنين. فالتدويرُ يمرّ من قفلٍ واحد للمتصفّح كلّه (Web Locks)، ومن يدخل بعد غيره يقرأ الرمز
 * الجديد الذي كتبه سابقُه — لا القديم. والمتصفّح الذي لا يعرف القفل يبقى على السلوك القديم.
 */
function acrossTabs<T>(fn: () => Promise<T>): Promise<T> {
  const locks = typeof navigator !== 'undefined' ? navigator.locks : undefined;
  return locks ? (locks.request('icontravel-refresh', () => fn()) as Promise<T>) : fn();
}

async function refreshOnce(): Promise<boolean> {
  const refreshToken = getRefreshToken();
  if (!refreshToken) {
    // لا رمز والتبويب كان داخلًا: خرج من تبويبٍ آخر — تعود شاشة الدخول لا شاشةٌ متجمّدة
    if (accessToken) { accessToken = null; notifySessionLost(); }
    return false;
  }
  let res: Response;
  try {
    res = await send('POST', '/auth/refresh', { refreshToken });
  } catch {
    // الشبكة مقطوعة: الجلسة **لا تُمسح** — تُعاد المحاولة مع الطلب التالي
    return false;
  }
  if (!res.ok) {
    /**
     * **401 وحده يُنهي الجلسة** (مراجعة 23/09/2026): كان أيُّ فشلٍ يمسحها — فخطأُ 502 لحظةَ إعادة
     * تشغيل الخادم عند النشر كان يُخرج من كان يعمل، ويترك شاشته قائمةً وكل حفظٍ فيها يفشل.
     */
    if (res.status === 401) {
      // تبويبٌ آخر دوّر الرمز بين قراءتنا وطلبنا؟ نجرّب بالجديد مرّةً قبل أن نحكم بالضياع
      const now = getRefreshToken();
      if (now && now !== refreshToken) return refreshOnce();
      setSession(null, null);
      notifySessionLost();
    }
    return false;
  }
  const j = (await parse(res)) as { accessToken: string; refreshToken: string };
  setSession(j.accessToken, j.refreshToken);
  return true;
}

export async function api<T = unknown>(method: string, path: string, body?: unknown, extra?: Record<string, string>): Promise<T> {
  const usedToken = accessToken;
  let res = await send(method, path, body, extra);
  if (res.status === 401 && accessToken) {
    // رمزٌ جديد وصل بعد أن خرج هذا الطلب؟ يُعاد به مباشرةً — لا تدويرٌ ثانٍ يُبطل الجديد
    if (accessToken !== usedToken) res = await send(method, path, body, extra);
    else if (await refreshSession()) res = await send(method, path, body, extra);
  }
  const data = await parse(res);
  if (!res.ok) {
    const e = (data as { error?: { code?: string; message?: string; details?: unknown } } | null)?.error;
    // ردٌّ بلا غلاف أخطاء الخادم (وسيط، أو خادم أقدم لا يعرف المسار): لا نبتلع الفشل في رسالة
    // عامة لا تدلّ على شيء — نذكر رمز الحالة، فيُعرف 404 من 500 من انقطاع الخادم بلا تخمين.
    // «بيانات غير صحيحة» وحدها لا تدلّ على الخانة: أوّلُ تفصيلٍ من الخادم يُلحق بها (مراجعة 27/09/2026)
    // واسمُ الخانة **بالعربي** من الخادم (label) لا مسارُها الإنجليزي — «limit: Number must be…» وصلت المالك (متوسط 18، 03/10/2026)
    const first = Array.isArray(e?.details) ? (e!.details as { label?: string; message?: string }[])[0] : undefined;
    const detail = e?.code === 'VALIDATION_ERROR' && first?.message ? ` — ${first.label ? first.label + ': ' : ''}${first.message}` : '';
    throw new ApiError(res.status, e?.code ?? `HTTP_${res.status}`,
      (e?.message ?? `تعذّر إتمام العملية — الخادم ردّ برمز ${res.status}`) + detail, e?.details);
  }
  return data as T;
}

/** رفع ملف واحد (multipart) — نفس معالجة الأخطاء وتجديد الجلسة. */
export const upload = <T>(path: string, form: FormData) => api<T>('POST', path, form);

/** تحميل ملف محمي: المسار يتطلب ترويسة Authorization، فلا يصلح كـ src مباشر. */
export async function getBlob(path: string): Promise<Blob> {
  const usedToken = accessToken;
  let res = await send('GET', path);
  if (res.status === 401 && accessToken) {
    if (accessToken !== usedToken) res = await send('GET', path);
    else if (await refreshSession()) res = await send('GET', path);
  }
  if (!res.ok) {
    const data = await parse(res);
    const e = (data as { error?: { code?: string; message?: string } } | null)?.error;
    throw new ApiError(res.status, e?.code ?? 'ERROR', e?.message ?? 'تعذّر فتح الملف');
  }
  return res.blob();
}

/**
 * تنزيل ملف محمي مع اسمه كما سمّاه الخادم. الاسم عربي فيصل مرمَّزًا في `filename*`
 * (RFC 5987)، ونعود إلى الاسم اللاتيني البسيط إن غاب.
 */
export async function downloadFile(path: string, fallbackName: string): Promise<void> {
  let res = await send('GET', path);
  if (res.status === 401 && accessToken) { if (await refreshSession()) res = await send('GET', path); }
  if (!res.ok) {
    const data = await parse(res);
    const e = (data as { error?: { code?: string; message?: string } } | null)?.error;
    throw new ApiError(res.status, e?.code ?? 'ERROR', e?.message ?? 'تعذّر إنشاء الملف');
  }
  const cd = res.headers.get('content-disposition') ?? '';
  const starred = /filename\*=UTF-8''([^;]+)/i.exec(cd)?.[1];
  const plain = /filename="([^"]+)"/i.exec(cd)?.[1];
  let name = fallbackName;
  if (starred) { try { name = decodeURIComponent(starred); } catch { name = plain ?? fallbackName; } }
  else if (plain) name = plain;

  const blob = await res.blob();

  // داخل تطبيق أندرويد: الـWebView لا ينفّذ رابط تنزيل ولا blob: — يُكتب الملف ثم يُشارَك.
  // المحتوى واحد في الحالتين: ما أرسله الخادم، بلا إعادة توليد ولا تعديل (قرار المالك D-6).
  if (isNativeApp()) { await saveFileNative(blob, name); return; }

  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export const get = <T>(path: string) => api<T>('GET', path);
export const post = <T>(path: string, body?: unknown, extra?: Record<string, string>) => api<T>('POST', path, body, extra);
export const patch = <T>(path: string, body: unknown, extra?: Record<string, string>) => api<T>('PATCH', path, body, extra);
export const put = <T>(path: string, body: unknown, extra?: Record<string, string>) => api<T>('PUT', path, body, extra);
export const del = <T>(path: string, extra?: Record<string, string>) => api<T>('DELETE', path, undefined, extra);

export const qs = (params: Record<string, string | number | boolean | null | undefined>): string => {
  const s = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) if (v !== undefined && v !== null && v !== '') s.set(k, String(v));
  const out = s.toString();
  return out ? `?${out}` : '';
};

// ---------- الأنواع المشتركة مع الـAPI ----------
export interface Me {
  id: string; email: string; fullNameAr: string; role: 'ADMIN' | 'SALES' | 'PARTNER';
  isOwner: boolean; mustChangePassword?: boolean;
  /** اعتماد تقييمات Google Map — صلاحيةٌ على الشخص لا على الدور (0068). */
  canApproveRatings?: boolean;
  /** «📢 إعلان» للكل وهو ليس أدمن — صلاحيةٌ على الشخص (0181، قرار المالك 03/10/2026: محمد إبراهيم). */
  canAnnounce?: boolean;
  /** يقرأ متابعات كل البياعين وهو ليس أدمن — قراءة فقط (0182، قرار المالك 03/10/2026: محمد إبراهيم). */
  canViewTeamFollowUps?: boolean;
  /** يحذف طلبات الموقع الواصلة له وهو ليس أدمن (0186، قرار المالك 04/10/2026: محمد إبراهيم). */
  canDeleteWebLeads?: boolean;
  /** أساسُ الأجر (0083): `WORKED_TIME` = أجرٌ بالوقت — فلا «حساباتي» له (قرار المالك 23/09/2026). */
  payBasis?: 'MONTHLY' | 'WORKED_TIME';
  /** هاتف صاحب الجلسة كما هو محفوظ — و`null`/غياب يعني **لا هاتف مسجَّل**، لا رقمًا يُخترع. */
  phone?: string | null;
}
export interface FollowUp {
  /** «تم الحجز» (01/10/2026): رقم حجز المتابعة، أو أوّل حجزٍ بالرقم نفسه منذ يوم التواصل */
  bookedBookingNo?: string | null;
  id: string; followUpNo: string; salesUserId: string; salesName: string; customerId: string | null;
  customerName: string; phone: string; contactedOn: string; contactedAt: string;
  /** رقمٌ تانٍ للعميل — واتساب أو اتصال (0183) */
  altPhone?: string | null;
  channel: string; result: string; customerStatus: string; notes: string | null;
  nextFollowUpOn: string | null; nextFollowUpAt: string | null; priority: string;
  bookingId: string | null; bookingNo: string | null; convertedAt: string | null;
  convertedToBooking: boolean; isOverdue: boolean; rowVersion: number;
  /** «✓ تمت المتابعة» (0151): سُجّل بعدها تواصلٌ لنفس العميل — والتواصل الذي أقفلها */
  closedAt?: string | null; closedByFollowUpId?: string | null;
  /** **من بنك الأرقام؟** (0154): الرقم سحبه البياع من بنكه — والحجز منه عليه مكافأة البنك */
  fromLeadBank?: boolean;
  /** مصدر المتابعة (01/10/2026): بنك الأرقام · طلب موقع · شات الموقع · من مصادر التواصل (مش من البنك ولا الموقع) */
  source?: 'LEAD_BANK' | 'WEB_LEAD' | 'SITE_CHAT' | 'MANUAL';
  /** لحظة تسجيلها (ISO) — تُعرض بساعة القاهرة بجانب التاريخ (01/10/2026) */
  createdAt?: string;
  /** متابعةٌ واحدة تتحدّث (01/10/2026): عدد مواقفها حتى هذا، ورقمها الأوّل */
  updatesCount?: number; firstFollowUpNo?: string;
}
/** طلبٌ وصل من نموذجٍ على موقع الشركة (0113) — ما كتبه العميل كما وصل، ولمن أُسند، وبماذا انتهى */
export interface WebLead {
  id: string; source: string; formName: string | null; customerName: string;
  phoneRaw: string | null; phoneE164: string | null; email: string | null; message: string | null;
  fields: Record<string, string>; receivedAt: string;
  assignedTo: string | null; assignedAt: string | null; assignedName: string | null;
  assignmentReason: 'ROTATION' | 'QUEUE' | 'ADMIN' | 'DESK' | null;
  followUpId: string | null; followUpNo: string | null; archivedAt: string | null;
  /** من سجّل المتابعة التي أقفلته ومتى — «مين ال رد عليها» (25/09/2026) */
  answeredName?: string | null; answeredAt?: string | null;
}
/** محادثة دعمٍ من الموقع (0117): من بدأها وماذا جمع البوت منه، وحالتها ومن يردّ عليه */
export interface ChatSession {
  id: string; externalId: string; customerName: string; phoneRaw: string | null; phoneE164: string | null;
  page: string | null; requestType: string | null; fields: Record<string, string>;
  status: 'WAITING' | 'ACTIVE' | 'CLOSED';
  assignedTo: string | null; assignedAt: string | null; agentName: string | null;
  firstReplyAt: string | null; visitorSeenAt: string; closedAt: string | null;
  closedReason: 'AGENT' | 'VISITOR' | 'TIMEOUT' | 'NO_AGENT' | null; webLeadId: string | null;
  startedAt: string; lastVisitorMessage: string | null; visitorMessageCount: number;
}
export interface ChatMessage {
  id: string; seq: number | string; sender: 'VISITOR' | 'AGENT' | 'SYSTEM'; body: string; createdAt: string; userName: string | null;
}
export interface ChatsResponse {
  waiting: ChatSession[]; active: ChatSession[]; waitMinutes: number;
  online: { id: string; fullNameAr: string }[];
  /** للأدمن: آخر ما أُقفل */
  recent?: ChatSession[];
}
/** شات الفريق (0128): جروب الشركة ورسائل خاصّة ومين أونلاين */
export interface TeamSummary { unread: number; lastSeq: number }
/** «النبضة» (27/09/2026): كل العدّادات في ردٍّ واحد كل عشر ثوانٍ — انظر lib/pulse.tsx */
export interface Pulse {
  serverNow: string;
  notifications: { unread: number; latestAt: string | null };
  chats: ChatSummary;
  team: TeamSummary;
  webLeads: { open: number; queued: number; latestAt: string | null };
}
export interface TeamPerson {
  id: string; fullNameAr: string; role: string; online: boolean; lastSeenAt: string | null; unread: number;
  /** نسخة صورته (وقت آخر تحديث) — null بلا صورة */
  photoVersion?: string | null;
  last: { body: string; at: string; mine: boolean } | null;
}
export interface TeamThreads {
  /** صورتي أنا — لتظهر لي كما تظهر لغيري */
  me?: { photoVersion: string | null };
  group: { unread: number; last: { body: string; senderName: string; at: string } | null; members: number; online: number };
  /** «💡 تطوير» (0147) — أفكار تحسين البرنامج */
  dev?: { unread: number; last: { body: string; senderName: string; at: string } | null };
  people: TeamPerson[];
}
export interface TeamMessage {
  id: string; seq: number | string; senderId: string; recipientId: string | null; body: string; createdAt: string; senderName: string;
  /** تعديل وحذف (0146) */
  editedAt?: string | null; deletedAt?: string | null;
  /** الردّ على رسالة (0150): الأصل المقتبس — من نفس المحادثة دائمًا */
  replyToId?: string | null; replySenderId?: string | null; replySenderName?: string | null;
  replyBody?: string | null; replyDeleted?: boolean; replyMime?: string | null;
  /** مرفق الرسالة (0138) — الملف من `/files/team-chat/:attachmentId` */
  attachmentId?: string | null; fileName?: string | null; mimeType?: string | null; sizeBytes?: number | string | null;
  /** التفاعلات (0156) — بترتيب أوّل من تفاعل */
  reactions?: TeamReaction[];
}
export interface TeamReaction { emoji: string; count: number; mine: boolean; names: string[] }
export interface ChatSummary { waiting: number; active: number; lastVisitorSeq: number }

/** إقرار مراجعة طيران بلا حجز (0139) */
export interface StandaloneDeclaration {
  id: string; createdBy: string; createdByName?: string; customerName: string; phoneRaw: string | null; phoneE164: string | null;
  passengerName: string | null; fromCity: string; toCity: string; transitCity: string | null;
  departureDate: string; returnDate: string | null; adults: number; children: number; infants: number; salePrice: string | null;
  airline: string | null; ticketType: string | null; cabin: string | null; tripKind: 'DOMESTIC' | 'INTERNATIONAL' | null;
  departTimeOut: string | null; departTimeBack: string | null; airportTimeOut: string | null; airportTimeBack: string | null;
  transitOut: string | null; transitBack: string | null; payMethod: string | null; fareRules: 'STANDARD' | 'NON_REFUNDABLE' | null;
  printedCount: number; lastPrintedAt: string | null; convertedBookingId: string | null; convertedBookingNo?: string | null;
  convertedAt: string | null; createdAt: string; updatedAt: string;
}

export interface WebLeadsResponse {
  items: WebLead[]; openCount: number;
  /** للأدمن وحده: كم ينتظر بلا صاحب، ومن هو أونلاين الآن بترتيب الدور */
  queuedCount?: number; online?: { id: string; fullNameAr: string }[];
}
export interface CustomerBrief {
  exists: boolean; previousName: string | null; bookingsCount: number | null; lastBookingDate: string | null;
  /** عميل بياعٍ آخر (02/10/2026): بلا تفاصيله — اسم بياعه وجملة الأمانة فقط */
  ownedByOther?: boolean; otherSellerName?: string | null; notice?: string | null;
}
export interface CrmLookup { phone: string; customer: CustomerBrief; followUps: { count: number; last: FollowUp | null } }
export interface HistoryResponse { phone: string; customer: CustomerBrief; activitiesCount: number; items: FollowUp[] }
export interface SellerSummary {
  date: string | null;
  today: { contacts: number; counted?: number; answered: number; noAnswer: number; unreachable: number; interested: number; readyToBook: number; notInterested: number; convertedToBooking: number; distinctCustomers: number };
  dueFollowUps: { dueToday: number; overdue: number };
  totals: { totalActivities: number; bookingsFromFollowUps: number };
}
export interface AdminSummary {
  totals: SellerSummary['today'] & { dueToday: number; overdue: number };
  bySeller: (SellerSummary['today'] & { salesUserId: string; salesName: string; dueToday: number; overdue: number })[];
}
/** مرفق حجز (أدلة تواصل العميل ومستندات الحجز) — سجل دائم، لا يُحذف ولا يُعدَّل. */
export interface BookingAttachment {
  id: string; category: string; description: string | null;
  originalFileName: string; mimeType: string; sizeBytes: number; sha256: string;
  supersedesId: string | null; uploadedBy: string; uploadedByName?: string; uploadedAt: string; isCurrent?: boolean;
  /** الاحتفاظ: يُحذف محتوى الملف فقط بعد 6 أشهر من تاريخ رفعه — البيانات والسجل يبقيان. */
  contentDeletedAt?: string | null; contentDeletedReason?: string | null; retentionExpiresOn?: string | null;
}

/** إثبات دفعة — سجل دائم لا يُحذف ولا يُعدَّل؛ الاستبدال بنسخة بديلة والأصل يبقى. */
export interface PaymentAttachment {
  id: string; originalFileName: string; mimeType: string; sizeBytes: number; sha256: string;
  description: string | null; supersedesId: string | null; uploadedBy: string; uploadedAt: string; isCurrent?: boolean;
  /** الاحتفاظ: يُحذف محتوى الملف فقط بعد 6 أشهر من تاريخ رفعه — البيانات والسجل يبقيان. */
  contentDeletedAt?: string | null; contentDeletedReason?: string | null; retentionExpiresOn?: string | null;
}

/** صورة تقييم العميل (0066) — دليلٌ ماليّ: لا تُعدَّل ولا تُحذف، والبديل نسخةٌ والأصل يبقى. */
export interface RatingEvidence {
  id: string; originalFileName: string; mimeType: string; sizeBytes: number; sha256: string;
  supersedesId: string | null; uploadedBy: string; uploadedByName?: string; uploadedAt: string; isCurrent?: boolean;
  contentDeletedAt?: string | null; contentDeletedReason?: string | null; retentionExpiresOn?: string | null;
}

export type RatingStatus = 'PENDING' | 'APPROVED' | 'REJECTED';

/** «مكافأة تقييم Google Map» في شهر (0066 و0068) — القاعدة تحسبها، والواجهة تعرض. */
export interface RatingBonus {
  month: string;
  /** المعتمد وحده — وهو وحده الذي عليه مال */
  ratedCount: number;
  /** ما زال ينتظر قرار المالك */
  pendingCount: number;
  rate: string; amount: string;
  highMin: number; highRate: string; lowMin: number; lowRate: string;
  /** كم تقييمًا يفصله عن السعر الأعلى — صفرٌ إن بلغه. */
  toHigher: number;
}
/** «مكافأة بنك الأرقام» في شهر (0107) — القاعدة تحسبها، والواجهة تعرض. */
export interface LeadBankBonusPolicy { threshold: string; underAmount: string; overAmount: string; effectiveFrom: string }
export interface LeadBankBonus {
  month: string; bookingsCount: number; underCount: number; overCount: number; amount: string;
  policy: LeadBankBonusPolicy | null;
}
export interface MyLeadBankBonus extends LeadBankBonus {
  bookings: { bookingId: string; bookingNo: string; customerName: string; businessDate: string; salePrice: string; amount: string }[];
}

export interface MyRatingBonus extends RatingBonus {
  ratings: { bookingId: string; bookingNo: string; customerName: string; submittedAt: string; decidedAt: string | null }[];
  mine: {
    bookingId: string; bookingNo: string; customerName: string; status: RatingStatus;
    submittedAt: string; decidedAt: string | null; decisionReason: string | null;
  }[];
}
export interface AdminRatingBonus {
  month: string;
  policy: { highMin: number; highRate: string; lowMin: number; lowRate: string } | null;
  items: (RatingBonus & { userId: string; fullNameAr: string })[];
  total: string;
}

/** صفّ في طابور اعتماد التقييمات — للمالك ورحمه وحدهما. */
export interface RatingQueueItem {
  bookingId: string; bookingNo: string; customerName: string; phone: string;
  salesUserId: string; salesName: string; status: RatingStatus;
  submittedAt: string; decidedAt: string | null; decidedByName: string | null; decisionReason: string | null;
  evidence: { id: string; originalFileName: string; mimeType: string }[];
}
export interface RatingQueue { items: RatingQueueItem[]; pendingCount: number }

/** نتيجة بحث البياع عن حجزه لرفع تقييمه. */
export interface BookingSearchRow {
  id: string; bookingNo: string; customerName: string; phoneE164: string;
  businessDate: string; operationType: string; ratingStatus: RatingStatus | null;
}

/** دفعة: سجل مستقل تمامًا عن الحجز، لا يُعدَّل ولا يُحذف. التصحيح عبر طلب إلغاء يعتمده الأدمن. */
/** رموز طرق الدفع كما يعرفها الخادم — والعملة يشتقّها الخادم من الطريقة، لا العميل. */
export type PaymentMethodCode =
  'INSTAPAY' | 'WALLET' | 'CASH' | 'BANK_TRANSFER_SAR' | 'VISA_OFFICE' | 'INDIRECT_INSTALLMENT'
  | 'CASH_USD' | 'BANK_TRANSFER_USD';

export interface Payment {
  id: string; paymentNo: string; bookingId: string;
  method: PaymentMethodCode;
  amount: string; currency: string; payerName: string | null; sourceAccount: string | null;
  /** «راحت لحساب مين» (0130) — رمزها وعنوانها */
  destinationCode?: string | null; destinationLabel?: string | null;
  collectedAtOffice: boolean | null; paidOn: string | null; note: string | null;
  createdAt: string; createdBy: string; createdByName: string;
  status: 'PENDING_REVIEW' | 'VERIFIED' | 'REJECTED' | 'VOIDED';
  verifiedAt: string | null; verifiedBy: string | null; verifiedByName: string | null;
  rejectedAt: string | null; rejectedBy: string | null; rejectedByName: string | null; rejectionReason: string | null;
  voidedAt: string | null; voidedBy: string | null; voidedByName: string | null; voidReason: string | null;
  voidRequestId: string | null; rowVersion: number;
  /** رسوم المحفظة فوق المبلغ (0166) — ما يُحسب للحجز = المبلغ − الرسوم */
  transferFee?: string;
  attachmentsCount: number; attachments: PaymentAttachment[];
}

/** المجاميع منفصلة: المتبقي = سعر البيع − المتحقق منه فقط. سجل الإلغاء بلا متبقي (null). */
export interface BookingPaymentSummary {
  bookingId: string; bookingNo: string; salePrice: string; operationType: string;
  paymentsCountTotal: number; pendingCount: number; verifiedCount: number; rejectedCount: number; voidedCount: number;
  totalRecorded: string; totalVerified: string; totalPending: string; totalRejectedVoided: string;
  remainingBalance: string | null; isOverpaid: boolean; pendingMissingEvidenceCount: number;
  /** كل عملة في مجاميعها المستقلة — لا تُجمع مع الجنيه ولا تُخصم من المتبقي بالجنيه. */
  paymentsCountSar: number; totalRecordedSar: string; totalVerifiedSar: string; totalPendingSar: string;
  paymentsCountUsd: number; totalRecordedUsd: string; totalVerifiedUsd: string; totalPendingUsd: string;
}

export interface PaymentVoidRequest {
  id: string; paymentId: string; bookingId: string; reason: string;
  requestedBy: string; requestedAt: string;
  status: 'PENDING' | 'APPROVED' | 'REJECTED';
  decidedBy: string | null; decidedAt: string | null; decisionNote: string | null;
}

export interface BookingPaymentsResponse {
  summary: BookingPaymentSummary | null; payments: Payment[]; voidRequests: PaymentVoidRequest[];
}

/**
 * كشف سجل العميل بالهاتف (R-4): الاسم السابق والعدد وآخر تاريخ ورقم آخر حجز — بلا اسم
 * بياع وبلا أي رقم مالي. يُقرأ وقت كتابة الرقم في نموذج الحجز لا وقت الحفظ.
 */
export interface CustomerLookup {
  phone: string; exists: boolean; previousName: string | null;
  bookingsCount: number | null; lastBookingDate: string | null; lastBookingNo: string | null;
  /** عميل بياعٍ آخر (قرار المالك 02/10/2026): «مكرر» ومع من وجملة الأمانة — بلا تفاصيله */
  ownedByOther?: boolean; notice?: string | null;
  /** اسم من أغلق آخر عملية مع هذا العميل — لا معرّفه ولا أي رقم من حجزه. */
  lastSellerName: string | null;
  matchedBy?: string | null;
  /** تاريخ ميلاده المسجَّل سابقًا (0085) — يملأ الخانة تلقائيًا فلا يُعاد كتابته. */
  dateOfBirth: string | null;
}

/**
 * بيانات إقرار المراجعة المحفوظة مع الحجز — ما ليس في النظام ويكتبه البياع مرّةً واحدة
 * (0048). النسخة الثانية تُطبع بما كُتب أوّل مرّة ولا يُملأ شيء من جديد.
 */
export interface BookingDeclaration {
  bookingId: string;
  passengerName: string | null; passportNo: string | null; airline: string | null;
  ticketType: string | null; cabin: string | null;
  tripKind: 'DOMESTIC' | 'INTERNATIONAL' | null;
  departTimeOut: string | null; departTimeBack: string | null;
  airportTimeOut: string | null; airportTimeBack: string | null;
  transitOut: string | null; transitBack: string | null; payMethod: string | null;
  /** شروط التذكرة (0135): STANDARD أو NON_REFUNDABLE؛ null للإقرارات القديمة = عادية */
  fareRules?: 'STANDARD' | 'NON_REFUNDABLE' | null;
  printedCount: number; lastPrintedAt: string | null;
  createdAt?: string; updatedAt?: string;
}
export interface BookingDeclarationResponse { declaration: BookingDeclaration | null }

/** «قيّمني» — تقييم البياع لنفسه في شهر (GET /me/evaluation). الحكم والإرشاد يُحسبان على الخادم. */
export interface EvalDay { date: string; followUps: number; answered: number; converted: number; leave: boolean }
export type EvalVerdict = 'EXCELLENT' | 'GOOD' | 'NEEDS_WORK' | 'WEAK';
/** مكافأة السنة: من ١/١ إلى ٣١/١٢ — تارجت الحجوزات الجديدة كاملًا ⇒ مكافأة أرباح. */
export interface YearBonus {
  year: number; from: string; to: string;
  newCount: number; target: number; remaining: number; achieved: boolean; amount: string;
}
export interface Evaluation {
  month: string; upTo: string; rule: { min: number; max: number };
  days: EvalDay[];
  followUps: {
    total: number; answered: number; interested: number; readyToBook: number; notInterested: number;
    converted: number; overdue: number; dueToday: number;
    workdays: number; leaveDays: number; daysMet: number; daysBelow: number; daysIdle: number; avgPerWorkday: number;
  };
  bookings: { newCount: number; repeatCount: number; total: number; newTarget: number | null; provisionalCommission: string };
  year: YearBonus;
  verdict: EvalVerdict; score: number;
  strengths: string[]; weaknesses: string[]; advice: string[];
}

/** ---------- مراجعة المدفوعات (الأدمن فقط) ---------- */
/** سطر في قائمة المراجعة كما يرجعه v_payment_review_queue — كلها دفعات بانتظار مراجعة يدوية. */
export interface ReviewQueueItem {
  paymentId: string; paymentNo: string; createdAt: string; waitingSeconds: number;
  method: PaymentMethodCode; amount: string; currency: string; payerName: string | null; sourceAccount: string | null;
  /** «راحت على» (0130) */
  destinationCode?: string | null; destinationLabel?: string | null;
  paidOn: string | null; collectedAtOffice: boolean | null;
  bookingId: string; bookingNo: string; customerName: string; operationType: string; salePrice: string;
  sellerId: string; sellerName: string;
  attachmentsCount: number; evidenceMissing: boolean;
  totalVerified: string; totalPending: string; remainingBalance: string | null;
}
/** إحصائيات قائمة المراجعة كاملةً (غير متأثرة بالفلاتر). */
export interface ReviewQueueStats {
  pendingCount: number; pendingAmount: string; pendingAmountSar: string; pendingAmountUsd: string;
  oldestCreatedAt: string | null; evidenceMissingCount: number;
}
/** ترقيم صفحات حقيقي على الخادم. total = عدد النتائج بعد تطبيق الفلاتر. */
export interface ReviewPage { offset: number; limit: number; total: number }
export interface ReviewQueueResponse { stats: ReviewQueueStats; page: ReviewPage; items: ReviewQueueItem[] }

/** سطر تدقيق كما يرجعه الخادم — سجل غير قابل للتعديل. */
/** سجل نسخة احتياطية كما يعيده الخادم — بلا اسم ملف وبلا أي مسار على القرص. */
export interface BackupEntry {
  id: string;
  kind: 'MANUAL' | 'AUTO';
  /** نُسخت خارج الجهاز (BACKUP_COPY_DIR) — قرار المالك 20/09/2026؛ وهل ضمّت المرفقات. */
  copiedAt: string | null;
  copyError: string | null;
  withFiles: boolean;
  status: 'RUNNING' | 'SUCCEEDED' | 'FAILED';
  sizeBytes: string | null;
  sha256: string | null;
  schemaVersion: string | null;
  startedAt: string;
  finishedAt: string | null;
  createdBy: string | null;
  createdByName: string | null;
  error: string | null;
}

/** فترة حافز كما يعيدها الخادم — نسبة New وحدها؛ لا نسبة Repeat ولا غيرها (الحافز = New فقط). */
/** حالة الفترة كما يشتقّها الخادم — الواجهة تعرضها ولا تحسبها. */
export type IncentiveState = 'SCHEDULED' | 'ACTIVE' | 'STOPPED' | 'EXPIRED' | 'CANCELLED';

export interface IncentivePeriodEntry {
  id: number; name: string; startDate: string; endDate: string;
  newRateBp: 2000 | 2500 | number;
  status: 'ACTIVE' | 'CANCELLED';
  createdBy: string | null; createdByName: string | null; createdAt: string; cancelledAt: string | null;
  /** النهاية المقرّرة قبل الإيقاف — للفترات الموقوفة وحدها. */
  plannedEndDate: string | null;
  stoppedAt: string | null; stoppedBy: string | null; stoppedByName: string | null;
  state: IncentiveState;
  /** الخادم وحده يقرّر أيّ فترة تقبل الإيقاف — الزرّ يتبعه ولا يجتهد. */
  canStop: boolean;
}
export interface IncentivesResponse { items: IncentivePeriodEntry[] }
export interface NewIncentiveBody { name: string; startDate: string; endDate: string; newRateBp: 2000 | 2500 }

export interface AuditEntry {
  occurredAt: string; actorUserId: string | null; actorRole: string; action: string;
  entityType: string; field: string | null; oldValue: string | null; newValue: string | null; reason: string | null;
}
/** تفاصيل الدفعة للأدمن: الدفعة وإثباتاتها + مجاميع الحجز + سجل التدقيق. */
export interface AdminPaymentDetail {
  payment: Payment; bookingSummary: BookingPaymentSummary | null; audit: AuditEntry[];
}
/** طلب إلغاء دفعة كما يرجعه /admin/payment-void-requests — يحمل حالة الدفعة الحالية لتقرير الأهلية. */
export interface AdminVoidRequest {
  id: string; paymentId: string; paymentNo: string; amount: string; currency: string;
  method: PaymentMethodCode;
  paymentStatus: 'PENDING_REVIEW' | 'VERIFIED' | 'REJECTED' | 'VOIDED';
  bookingId: string; bookingNo: string; customerName: string;
  reason: string; requestedBy: string; requestedByName: string; requestedAt: string;
  status: 'PENDING' | 'APPROVED' | 'REJECTED';
  decidedBy: string | null; decidedAt: string | null; decisionNote: string | null;
}

/** مستخدم كما يرجعه /admin/users — يُستخدم لفلتر البياع ولإظهار أسماء فاعلي التدقيق. */
export type WorkLocation = 'OFFICE' | 'HOME' | 'HYBRID';

export interface AdminUser {
  id: string; email: string; fullNameAr: string; fullNameEn: string | null; sheetAlias: string | null;
  role: 'ADMIN' | 'SALES' | 'PARTNER'; isOwner: boolean; isActive: boolean;
  // ملف الموظف — الرقم الوظيفي تولّده القاعدة ولا يُحرَّر
  employeeNo?: string; phone?: string | null; hireDate?: string | null;
  workLocation?: WorkLocation | null; adminNotes?: string | null;
  /** أيام البيت لموظف المكتب (0118): 0 الأحد … 5 الجمعة، 6 السبت */
  remoteWeekdays?: number[];
  planCode?: string | null; attendanceEnrolled?: boolean;
  lastLoginAt?: string | null; createdAt?: string;
}

/** صفّ كشف المرتبات: المرتب مُسجَّل، والعمولة تُقرأ من التسوية ولا تُحسب هنا. */
export interface SalaryRow {
  userId: string; employeeNo: string; fullNameAr: string; role: 'ADMIN' | 'SALES' | 'PARTNER'; isActive: boolean;
  salary: string | null; note: string | null; salaryUpdatedAt: string | null;
  /** null = لم تُحسب تسوية هذا الشهر بعد — لا يساوي صفرًا. */
  commission: string | null; settlementStatus: string | null;
  /** مكافأة التقييمات (0066): صفرٌ صريح لمن لا تقييم محسوب له، لا null. */
  ratingBonus: string; ratedCount: number;
  /** مكافأة بنك الأرقام (0107): كذلك صفرٌ صريح. */
  leadBankBonus: string; leadBankBookings: number;
}

export interface ExpenseLine {
  code: string; labelAr: string;
  /** null = لم يُسجَّل هذا البند لهذا الشهر — وهو غير الصفر. */
  amount: string | null; note: string | null; updatedAt: string | null;
}

export interface MonthlyExpensesReport {
  month: string;
  lines: ExpenseLine[];
  recordedCount: number;
  missingLabels: string[];
  expensesTotal: string;
  bookings: { count: number; revenue: string; grossProfit: string };
  commissions: { approved: string; pendingCount: number };
  /** مكافأة التقييمات المستحقّة هذا الشهر لكل البياعين (0066) — تُخصم كالعمولة. */
  ratingBonus: { total: string; sellers: number; ratedCount: number };
  /** مكافأة بنك الأرقام (0107) — تُخصم كذلك. */
  leadBankBonus: { total: string; sellers: number; bookings: number };
  salaries: { total: string; recordedCount: number; employees: number };
  /** null = لم تُسجَّل مصاريف هذا الشهر، فلا صافي يُعرض. */
  net: string | null;
  recentMonths: { month: string; recordedCount: number }[];
}

/**
 * «الشريط المتحرك» — رسالة الأدمن التي تمشي أمام الجميع (0071). أحدث نسخة هي الظاهرة.
 * `speedSeconds` (0072): ثوانٍ لدورة النصّ الكاملة، بين 5 و60 — أكبر يعني أبطأ.
 */
export interface TickerMessage {
  id: number | null; body: string; active: boolean; link: string | null;
  speedSeconds: number; updatedAt: string | null;
}
export interface TickerHistoryRow {
  id: number; body: string; active: boolean; link: string | null; speedSeconds: number;
  createdAt: string; createdByName: string | null;
}

export interface FxRate {
  currencyCode: string; nameAr: string; unit: number; buy: string; sell: string; sortOrder: number;
  /** سعر التحصيل عندنا = شراء البنك − 0.25 — للدولار والريال وحدهما، وnull لغيرهما. */
  ourRate: string | null;
  /** وسعر بيعنا للعميل = بيع البنك + 1.50 — للعملتين نفسيهما. */
  ourSell: string | null;
}
export interface FxReport {
  /** null = لا سعر محفوظ بعد. */
  rateDate: string | null;
  fetchedAt: string | null;
  ageDays: number | null;
  rows: FxRate[];
  /** نصّ آخر محاولة تحديث فاشلة — يُعرض ولا يُخفى. */
  error?: string;
}

export interface KpiCounts {
  NEW: number; REPEAT: number; MODIFICATION: number; CANCELLATION: number; COMPANY: number; PRELIMINARY?: number;
  total: number; crm: number;
}
export interface KpiRow {
  userId: string; fullNameAr: string; role: 'ADMIN' | 'SALES' | 'PARTNER';
  day: KpiCounts; monthToDate: KpiCounts;
  /** للأدمن وحده — الخادم لا يرسله للموظف أصلًا. */
  grossProfit: string | null;
  newTarget: number | null;
}
export interface KpiReport {
  scope: 'ADMIN' | 'SELF';
  month: string; upTo: string; newTarget: number | null; rows: KpiRow[];
  /** تارجت المتابعات اليومي وحدّ مكافأته (0092) — من القاعدة السارية لا من الشيفرة. */
  crmDailyTarget: number | null;
  crmHighTarget: number | null;
  /** طلبات الموقع ومحادثات الشات في نفس النافذة (25/09/2026) — اختيارية: خادمٌ أقدم لا يرسلها. */
  website?: WebsiteKpi;
}

export interface WebsiteCount { label: string; count: number }
export interface WebsiteKpi {
  from: string; to: string;
  leads: { total: number; contacted: number; byForm: WebsiteCount[]; byPage: WebsiteCount[]; bySource: WebsiteCount[] };
  chats: { total: number; answered: number; answeredFast: number; noAgent: number; becameLead: number; byPage: WebsiteCount[] };
  byUser: { userId: string; fullNameAr: string; leads: number; contacted: number; chats: number; answered: number }[];
}

export interface LookupOption { code: string; labelAr: string; labelEn?: string | null }
/** قسيمة الفندق المحفوظة مع الحجز (0058) — ما لا يعرفه النظام من الورقة. */
export interface BookingHotelVoucher {
  bookingId: string;
  branch: string | null; hotelName: string | null; hotelCity: string | null;
  roomType: string | null; viewType: string | null; mealPlan: string | null; rooms: number | null;
  /** نجوم الفندق (0172) */
  hotelStars?: number | null;
  childrenAges: string | null; seats: number | null; transport: string | null;
  altPhone: string | null; collectAt: string | null; notes: string | null;
  printedCount: number; lastPrintedAt: string | null;
}
export interface BookingHotelVoucherResponse { voucher: BookingHotelVoucher | null }

/** مكتب الشركة (0055): موقعه ونطاقه — تُقاس به مسافة كل بصمة. */
export interface OfficeLocation {
  id: string; nameAr: string; lat: number; lon: number; radiusM: number;
  addressAr?: string | null; isActive?: boolean;
}

export interface Lookups {
  bookingTypes: LookupOption[]; executionSources: LookupOption[]; customerRatings: LookupOption[];
  nationalities: LookupOption[]; operationTypes?: LookupOption[];
  /** «راحت لحساب مين» (0130) */
  paymentDestinations?: LookupOption[];
}

/** الحجز كما يرجعه الخادم: الحقول التجارية + المحسوبة على الخادم. هامش الربح وGross Profit للأدمن فقط. */
export interface Booking {
  /** حالة مراجعة «تصفية الحساب» (0167) — للأدمن وحده */
  settlementReviewStatus?: SettlementReviewStatus;
  /** كم مرفقًا/ملحوظةً أُضيفت في التصفية منذ آخر مراجعة (0185) — للأدمن، رقمٌ على زرّ «تصفية» */
  settlementNewCount?: number;
  id: string; bookingNo: string; customerName: string; phone: string; salePrice: string; operationType: string;
  /** رقمٌ تانٍ للعميل — واتساب أو اتصال (0183) */
  altPhone?: string | null;
  /** تاريخ ميلاد العميل (0085) — من صفّ العميل لا من الحجز. */
  dateOfBirth?: string | null;
  /** اسم الشركة الدافعة (0088) — لحجوزات «شركة» وحدها، والقاعدة تمنعه في غيرها. */
  companyName?: string | null;
  salesUserId?: string; salesName?: string;
  bookingType?: string; hotelOrVisa?: string | null; nationality?: string | null;
  fromCity?: string | null; transitCity?: string | null; toCity?: string | null;
  adults?: number; children?: number; infants?: number;
  departureDate?: string; returnDate?: string | null;
  costPrice?: string;
  /** حجز الفندق (0051): مكوّنات التكلفة — والمجموع هو `costPrice` نفسه محسوبًا على الخادم */
  hotelCost?: string | null; busSeats?: number | null; busSeatCost?: string | null;
  costWithTax?: string | null;                 // 17 — محسوب على الخادم
  commissionRatePct?: string | null;           // 18 — محسوب على الخادم
  provisionalCommission?: string | null;       // 19 — محسوب على الخادم
  commissionRateSource?: string | null; commissionableAmount?: string | null; commissionableIsNegative?: boolean | null;
  executionSource?: string | null; customerRating?: string | null;
  /** مصدر التنفيذ لكل جزء في الحجز المركّب (0124) — فارغة في غير المركّب */
  executionSourceHotel?: string | null; executionSourceFlight?: string | null; executionSourceVisa?: string | null;
  isOwnerBooking?: boolean; status?: string; businessDate?: string; createdAt?: string;
  editWindowEndsAt?: string; editableBySeller?: boolean; rowVersion?: number;
  relatedBookingId?: string | null; relatedBookingNo?: string | null; relationType?: string | null; externalReference?: string | null;
  grossProfitMarginPct?: string | null;        // 23 — للأدمن فقط
  grossProfit?: string | null;                 // 24 — للأدمن فقط
}

/**
 * ملاحظةٌ على الحجز (0111) — سطرٌ في دفتر وقائعه، عليه اسمُ كاتبه ووقتُه.
 * ولا تُعدَّل ولا تُحذف: فلا حقول لذلك هنا ولا مسار لها في الخادم.
 */
export interface BookingNote {
  id: string; body: string; createdAt: string;
  authorId?: string; authorName?: string | null;
}

/**
 * «العميل ده ليه رصيد كذا» (0112) — **تذكرةٌ مكتوبة لا حساب**.
 *
 * لا تدخل عمولةً ولا خزنةً ولا متبقّي حجز. فمن وجدها هنا وأراد أن يجمعها مع مالٍ
 * آخر في شاشةٍ فليقرأ رأس الهجرة 0112 أوّلًا — الربطُ يغيّر أرقامًا اعتمدها المالك.
 */
export interface CreditNote {
  id: string; amount: string; reason: string | null; createdAt: string;
  authorName?: string | null; bookingNo?: string | null;
  customerName?: string | null; customerPhone?: string | null;
  settledAt: string | null; settledAmount?: string | null;
  settledNote?: string | null; settledByName?: string | null;
  /** المسدَّد والباقي ودفعاتُ السداد (0163) — السداد على دفعات بسكرينة التحويل */
  paidTotal?: string; remaining?: string;
  payments?: CreditPayment[];
}
export interface CreditPayment {
  id: string; amount: string; note: string | null; paidAt: string; paidByName: string | null;
  hasScreenshot: boolean; mimeType: string | null;
}

// ---------- التسويات الشهرية والعمولات (Phase 4) ----------
export type SettlementStatus =
  'OPEN' | 'PENDING_EDITS' | 'CALCULATED' | 'ADMIN_REVIEW' | 'READY_FOR_APPROVAL' | 'APPROVED' | 'LOCKED' | 'SUPERSEDED';
export type RateSource = 'OWNER' | 'BASE' | 'INCENTIVE' | 'TIER_A' | 'TIER_B' | 'TIER_C';
export type SettlementLineStatus = 'INCLUDED' | 'EXCLUDED_ADMIN_REVIEW' | 'NO_COMMISSION';
export type ReviewCaseType = 'NEGATIVE_COMMISSIONABLE' | 'POST_LOCK_CHANGE' | 'TIER_CONFIG_GAP';

/** رأس التسوية كما خُزّن. baseTotal يرجع للأدمن فقط — وهو قيمة تدقيق لا عمولة مستحقة ثانية. */
export interface Settlement {
  id: string; month: string; revision: number; status: SettlementStatus;
  matchedTierCode: string | null; targetNewCount: number; achievementPct: string;
  finalCommissionTotal: string; bonusAmount: string; adjustmentsTotal: string; totalPayable: string | null;
  calculatedAt: string | null; approvedAt: string | null;
  userId?: string; sellerName?: string; baseTotal?: string; approvedByName?: string | null;
  explanation?: { planCode?: string; counts?: Record<string, number>; notes?: string[] };
  supersedesId?: string | null; createdAt?: string;
}

export interface SettlementLine {
  bookingId: string; bookingNo: string; operationType: string;
  sale: string; costWithTax: string; commissionable: string;
  appliedRateBp: number; rateSource: RateSource; commission: string;
  lineStatus: SettlementLineStatus; incentivePeriodId: number | null;
  baseRateBp?: number; ruleVersionId?: number | null;
  /** اسم العميل — يرجع في مسار الأدمن فقط. */
  customerName?: string | null;
}

export interface SettlementAdjustment {
  id: string; amount: string; reason: string;
  originBookingId: string | null; originSettlementId: string | null;
  createdAt: string; createdBy: string | null; createdByName?: string | null;
}

export interface ReviewCase {
  id: string; type: ReviewCaseType; status: 'OPEN' | 'RESOLVED';
  userId: string | null; sellerName?: string | null; month: string | null;
  bookingId: string | null; bookingNo?: string | null; settlementId: string | null;
  details: Record<string, unknown> | null;
  resolution: string | null; resolvedBy?: string | null; resolvedAt: string | null; createdAt: string;
}

export interface SettlementDetail {
  settlement: Settlement; lines: SettlementLine[];
  adjustments: SettlementAdjustment[]; reviewCases: ReviewCase[];
}
export interface SettlementsResponse { page: ReviewPage; items: Settlement[] }
export interface ReviewCasesResponse { page: ReviewPage; items: ReviewCase[] }

// ---------- الإشعارات وسجل التدقيق (Phase 7A) ----------
// المسارات الثلاثة قائمة منذ 2B ولم تتغيّر: الواجهة تعرضها فقط.
// ولا قناة هنا غير IN_APP — Push وواتساب مؤجَّلتان بقرار المالك (7B/7C).

export type NotificationSeverity = 'INFO' | 'WARNING' | 'CRITICAL';

export const ANNOUNCEMENT_TYPE = 'ADMIN_ANNOUNCEMENT';

export interface AppNotification {
  id: string; type: string; severity: NotificationSeverity;
  title: string; body: string | null;
  payload: Record<string, unknown> | null;
  bookingId: string | null; createdAt: string; readAt: string | null;
  /** إخفاء لصاحبه وحده — الصفّ باقٍ في القاعدة وسجل الأدمن كامل. */
  dismissedAt?: string | null;
  /** اسم كاتب الإعلان الإداري؛ وnull لما يولّده النظام. */
  senderName?: string | null;
  senderIsOwner?: boolean;
}
export interface NotificationsResponse { unreadCount: number; items: AppNotification[] }

export interface AnnouncementLogRow {
  title: string; body: string; severity: NotificationSeverity; createdAt: string;
  senderName: string | null; recipients: number; readCount: number; dismissedCount: number;
}

// ---------- تحليل الأداء الإداري (الأدمن وحده) ----------
export type AnalyticsPeriod = 'THIS_MONTH' | 'LAST_MONTH' | 'LAST_3M' | 'LAST_6M' | 'THIS_YEAR' | 'CUSTOM';

export interface AnalyticsCounts {
  NEW: number; REPEAT: number; MODIFICATION: number; CANCELLATION: number; COMPANY: number; PRELIMINARY?: number; total: number;
}
export interface AnalyticsTotals {
  counts: AnalyticsCounts; revenue: string; cost: string; grossProfit: string;
  marginPct: string | null; crm: number;
}
export interface AnalyticsDelta { pct: number | null; current: number; previous: number }
export interface AnalyticsEmployee {
  userId: string; fullNameAr: string; role: 'ADMIN' | 'SALES' | 'PARTNER';
  /** false = المالك/الأدمن: يظهر بحجوزاته وربحه بلا عمولة ولا تارجت. */
  commissionable: boolean;
  counts: AnalyticsCounts; revenue: string; grossProfit: string; crm: number;
  newTarget: number | null; targetPct: number | null; remainingToTarget: number | null;
  attendance: {
    days: number; onTime: number; late: number;
    punctualityPct: number | null; avgLateMinutes: number | null; measurable: boolean;
    /** **مجموع** دقائق التأخير فوق السماح — المتوسط يخفي من تأخّر مرّةً ساعتين. */
    lateMinutesTotal?: number;
    /** يوم عملٍ مجدول بلا حضورٍ ولا إجازة معتمدة. */
    absentDays?: number;
    leaveDays?: number;
  };
  /** النتيجة لا المجهود: تحويل المتابعات · نسبة الإلغاء · الربح لكل يوم حضور. */
  extra: {
    convertedFollowUps: number;
    conversionPct: number | null;
    cancellationPct: number | null;
    profitPerWorkday: string | null;
  };
}
export interface AnalyticsTrendPoint { month: string; newCount: number; total: number; revenue: string; grossProfit: string; collectedEgp: string }
/** التحصيل المعتمَد لكل عملة — لا تُجمع عملتان في رقم واحد ولا سعر صرف في النظام. */
export interface AnalyticsCollected { currency: 'EGP' | 'SAR' | 'USD'; amount: string; payments: number }
export interface AnalyticsBreakdown { code: string; label: string; count: number; revenue: string; grossProfit: string; marginPct: string | null }
export interface AnalyticsRanking {
  metric: string; label: string;
  leaders: { userId: string; fullNameAr: string; value: string }[];
}
// ---------- أقسام القرار: صافي الأرباح · المتأخرات · العملاء · الوجهات · الدفع · الشواذ · المهلة ----------
//
// قاعدةٌ واحدة تحكم هذه الأنواع كلها: **ما لا أساس له يأتي `null` لا صفرًا**. صفرٌ في
// شاشة إدارة يُقرأ حقيقةً («لا متأخرات»، «لا أحد يعود») بينما معناه «لا نعلم» — وهذا
// أخطر من ألّا يُعرض شيء. فكل `| null` هنا مقصودٌ ويُعرض نصًّا يشرح سببه.
export interface AnalyticsNetProfit {
  grossProfit: string;
  /** المعتمَدة وحدها؛ والمعلّقة تُعدّ في `pendingSettlements` ولا تُخصم. */
  commissions: string;
  pendingSettlements: number;
  salaries: string; ratingBonus: string; leadBankBonus: string; expenses: string;
  /** null ⇒ شهرٌ في المدى بلا مصاريف مسجَّلة، فلا صافي يُعرض. */
  net: string | null;
  monthsMissingExpenses: string[];
  marginPct: number | null;
}
export interface AnalyticsAgingBucket { code: string; label: string; bookings: number; amount: string }
export interface AnalyticsOverdue {
  bookingId: string; bookingNo: string; customerName: string; phone: string;
  salesName: string; amount: string; daysOld: number;
}
export interface AnalyticsReceivables {
  totalEgp: string;
  buckets: AnalyticsAgingBucket[];
  oldest: AnalyticsOverdue[];
  avgDaysToSettle: number | null;
  settledBookings: number;
}
export interface AnalyticsTopCustomer {
  customerName: string; phone: string; bookings: number; revenue: string; lastBooking: string;
}
export interface AnalyticsWinBack extends AnalyticsTopCustomer { salesName: string; monthsSince: number }
export interface AnalyticsCustomers {
  newCustomers: number; returningCustomers: number; returnRatePct: number | null;
  top: AnalyticsTopCustomer[];
  winBack: AnalyticsWinBack[];
}
export interface AnalyticsDestination {
  city: string; count: number; revenue: string; grossProfit: string; marginPct: number | null;
}
export interface AnalyticsPaymentMethod {
  method: string; label: string; currency: string; payments: number; amount: string;
}
export interface AnalyticsMarginOutlier {
  bookingId: string; bookingNo: string; customerName: string; salesName: string;
  businessDate: string; revenue: string; grossProfit: string; marginPct: number;
}
export interface AnalyticsMarginOutliers {
  averageMarginPct: number | null; thresholdPct: number | null; rows: AnalyticsMarginOutlier[];
}
export interface AnalyticsLeadTime {
  avgDays: number | null; medianDays: number | null;
  buckets: { label: string; count: number }[];
}

export interface AnalyticsReport {
  period: AnalyticsPeriod;
  from: string; to: string;
  previous: { from: string; to: string } | null;
  totals: AnalyticsTotals;
  previousTotals: AnalyticsTotals | null;
  deltas: { newCount: AnalyticsDelta; total: AnalyticsDelta; revenue: AnalyticsDelta; grossProfit: AnalyticsDelta } | null;
  /** null = لا بيانات كافية للمقارنة. */
  direction: 'UP' | 'DOWN' | 'FLAT' | null;
  collected: AnalyticsCollected[];
  outstandingEgp: string;
  lastYear: { from: string; to: string; totals: AnalyticsTotals } | null;
  lastYearDeltas: { total: AnalyticsDelta; revenue: AnalyticsDelta; grossProfit: AnalyticsDelta } | null;
  bySource: AnalyticsBreakdown[];
  byBookingType: AnalyticsBreakdown[];
  trend: AnalyticsTrendPoint[];
  employees: AnalyticsEmployee[];
  rankings: AnalyticsRanking[];
  insights: string[];
  newTarget: number | null;
  netProfit: AnalyticsNetProfit;
  receivables: AnalyticsReceivables;
  customers: AnalyticsCustomers;
  destinations: AnalyticsDestination[];
  paymentMethods: AnalyticsPaymentMethod[];
  marginOutliers: AnalyticsMarginOutliers;
  leadTime: AnalyticsLeadTime;
  birthdays: AnalyticsBirthdays;
}

/**
 * صف في سجل التدقيق كما يعيده الخادم.
 * التقنيع يقع **عند الكتابة** (trg_audit_row يكتب '***' للحقول السرّية، و0029 يُفرّغ أثر
 * الأجهزة من صفوف الحضور) — فالواجهة تعرض ما وصلها ولا تكشف شيئًا ولا تُلغي تقنيعًا.
 */
export interface AuditRow {
  id: number; occurredAt: string;
  actorUserId: string | null; actorRole: string; action: string;
  entityType: string; entityId: string | null; bookingNo: string | null;
  field: string | null; oldValue: string | null; newValue: string | null;
  reason: string | null; ip: string | null; requestId: string | null;
  metadata: Record<string, unknown> | null;
}

// ---------- سياسة التارجت وشرائحها (Phase 6 — Step 6C) ----------
// الشريحة تُحدَّد بعدد New وحده — **لا حقل Repeat في أي نوع هنا** (قاعدة معتمدة §6.5).
// ولا تعديل: التغيير يكون بنسخة جديدة بتاريخ سريان، والقائم ثابت.

export type TierEffectCode = 'ZERO_ALL' | 'FLAT_ALL' | 'OVERRIDE_NEW' | 'FULL';

export interface PolicyTier {
  id?: number; code: string; newMin: number; newMax: number | null;
  effect: TierEffectCode; rateBp?: number | null; grantsBonus: boolean;
}

export interface TargetPolicyVersion {
  id: number; versionNo: number; effectiveFrom: string; effectiveTo: string | null;
  newTarget: number; bonusAmount: string; currency: string; note: string | null;
  createdAt: string; createdByName: string | null;
  isCurrent: boolean; tiers: PolicyTier[];
}
export interface TargetPoliciesResponse { items: TargetPolicyVersion[]; current: TargetPolicyVersion | null }
/** السارية الآن كما يراها البياع (02/10/2026) — بلا ملاحظة الأدمن ولا من أنشأها */
export interface MyTargetPolicy { versionNo: number; effectiveFrom: string; newTarget: number; bonusAmount: string; currency: string; tiers: PolicyTier[] }

/** «مكافأة أرباح سنوية» — نسخ السياسة (0065): أحدث نسخة لسنةٍ هي الساري فيها. */
export interface AnnualBonusVersion {
  id: number; year: number; newTarget: number; bonusAmount: string; note: string | null; createdAt: string;
}
export interface AnnualBonusResponse {
  thisYear: number; items: AnnualBonusVersion[]; current: AnnualBonusVersion[];
}

/** جسم إنشاء نسخة جديدة — بلا أي حقل Repeat، والخادم صارم فأي حقل زائد يُرفض بـ400. */
export interface NewTargetPolicyBody {
  effectiveFrom: string; newTarget: number; bonusAmount: string; note?: string | null;
  tiers: { code: string; newMin: number; newMax: number | null; effect: TierEffectCode; rateBp?: number | null; grantsBonus: boolean }[];
}

// ---------- لوحة الأدمن ولوحة الترتيب (Phase 6 — Step 6A) ----------
// كل رقم مالي هنا يأتي محسوبًا من الخادم من لقطة التسوية — الواجهة تعرضه ولا تحسبه،
// ولا تجمع عمولة ولا تارجت ولا حافزًا. والعدّ ونسبة التحقيق والحدّ كلها من الخادم كذلك.

export type OperationTypeCode = 'NEW' | 'REPEAT' | 'MODIFICATION' | 'CANCELLATION' | 'COMPANY' | 'PRELIMINARY';
export type OperationCounts = Record<OperationTypeCode, number> & { total: number };

/** أرقام لقطة التسوية كما خُزّنت. null في السطر يعني: لم تُحسب تسوية هذا الشهر بعد. */
export interface DashboardSettlementFacts {
  id: string; status: SettlementStatus; matchedTierCode: string | null;
  targetNewCount: number; achievementPct: string;
  finalCommissionTotal: string; bonusAmount: string; adjustmentsTotal: string; totalPayable: string | null;
  calculatedAt: string | null; approvedAt: string | null;
}

export interface DashboardRow {
  userId: string; fullNameAr: string; role: 'ADMIN' | 'SALES' | 'PARTNER';
  /** false = المالك/الأدمن: صفٌّ بحجوزاته وربحه، وأعمدة العمولة والتارجت فارغة بصدق. */
  commissionable: boolean;
  counts: OperationCounts; grossProfit: string;
  newTarget: number | null; remainingToTarget: number | null;
  settlement: DashboardSettlementFacts | null;
}

export interface AdminDashboard {
  month: string; newTarget: number | null; rows: DashboardRow[];
  totals: { counts: OperationCounts; grossProfit: string; finalCommissionTotal: string; bonusAmount: string; totalPayable: string };
  notCalculated: string[];
}

export type LeaderboardOrder = 'new' | 'achievement';

export interface LeaderboardEntry {
  rank: number; userId: string; fullNameAr: string;
  newCount: number; repeatCount: number; achievementPct: string; matchedTierCode: string | null;
  finalCommissionTotal: string; bonusAmount: string; totalPayable: string | null;
}
export interface AdminLeaderboard {
  month: string; orderBy: LeaderboardOrder; newTarget: number | null;
  participants: number; entries: LeaderboardEntry[]; notCalculated: { userId: string; fullNameAr: string }[];
}
/**
 * حمولة البياع (قرار المالك R-11): ترتيبه ونسبة تحقيقه فقط.
 * لا مصفوفة متسابقين ولا أي رقم مالي لأي أحد — الخادم لا يرسلها أصلًا، فالنوع يطابق ذلك.
 */
export interface SellerLeaderboard {
  month: string; orderBy: LeaderboardOrder; participants: number;
  me: { rank: number; achievementPct: string } | null;
}

/** تقرير المدفوعات القائم منذ 2B — يُعرض كما هو بلا حساب ثانٍ. */
export type PaymentsReportGroupBy = 'seller' | 'method' | 'status' | 'day' | 'month';
export interface PaymentsReportTotals {
  paymentsCount: number; totalRecorded: string; totalVerified: string;
  totalPending: string; totalRejectedVoided: string; pendingCount: number;
}
export interface PaymentsReportResponse {
  totals: PaymentsReportTotals;
  groupBy: PaymentsReportGroupBy | null;
  groups: (PaymentsReportTotals & { groupKey: string })[];
}

// ---------- الحضور والانصراف (Phase 5 — Step 7) ----------
// لا حقل deviceInfo في أي نوع هنا ولا في أي ملف من ملفات الوحدة: قرار المالك D-2 رفضه رفضًا
// كاملًا — لا جمعًا ولا إرسالًا ولا عرضًا ولا تعريف نوع. الخادم يبقى هو من يحصر الحقل بالأدمن،
// والواجهة لا تطلبه أصلًا. والطوابع كلها من الخادم: لا مسار هنا يرسل وقتًا.

export type AttendanceKind = 'CHECK_IN' | 'CHECK_OUT';
export type EventValidity = 'VALID' | 'INVALID';
export type RecordStatus =
  | 'ON_LEAVE' | 'NOT_STARTED' | 'IN_PROGRESS' | 'RECORDED'
  | 'MISSING_CHECK_IN' | 'MISSING_CHECK_OUT' | 'ABSENT';
export type Compliance = 'COMPLETE' | 'SHORT' | 'UNDETERMINED' | 'NOT_APPLICABLE';
export type RecordStatusSource = 'DERIVED' | 'ADMIN_CLASSIFIED';
export type DayClassification = 'ABSENT' | 'MISSING_CHECK_IN' | 'LEAVE';
export type LeaveStatus = 'PENDING' | 'APPROVED' | 'REJECTED' | 'CANCELLED';
export type AttendanceAnomaly = 'WORKED_WHILE_ON_LEAVE' | 'MISSING_LOCATION' | 'MULTIPLE_UNMATCHED_EVENTS';

/** حدث حضور كما يعيده الخادم. الإحداثيات تُفرَّغ بعد ثلاثة أشهر ويبقى locationPurgedAt شاهدًا. */
export interface AttendanceEvent {
  id: string; kind: AttendanceKind; occurredAt: string; anchorDate: string; businessDate: string;
  gpsLat: number | null; gpsLon: number | null; gpsAccuracyM: number | null;
  locationMissing: boolean; locationDeniedReason: string | null; locationPurgedAt: string | null;
  validity: EventValidity; invalidatedAt: string | null; invalidReason: string | null;
}

/** الجدول المرن بلا وردية ولا سماح — الحقول غائبة لا فارغة (نفس schedule_kind_shape). */
export interface AttendanceSchedule {
  kind: 'FIXED' | 'FLEXIBLE'; expectedMinutes: number; source: string;
  startLocal?: string; endLocal?: string; crossesMidnight?: boolean; graceMinutes?: number;
}

export interface EventPair { checkInEventId: string; checkOutEventId: string; startedAt: string; endedAt: string; minutes: number }

export interface DayClassificationInfo {
  value: DayClassification; previousValue: DayClassification | null;
  reason: string; changedBy: string; changedAt: string;
}

/** يوم مشتق. lateMinutes للجدول الثابت وحده — غائب تمامًا عن المرن، والغياب ليس صفرًا. */
export interface AttendanceDay {
  anchorDate: string; dayIsFinal: boolean; provisional: boolean;
  recordStatus: RecordStatus; recordStatusSource: RecordStatusSource;
  compliance: Compliance; expectedMinutes: number; remainingMinutes: number | null;
  workedMinutes: number; workedMinutesPartial: boolean;
  checkedIn: boolean; firstCheckInAt: string | null;
  checkedOut: boolean; lastCheckOutAt: string | null;
  /** آخرُ حضور وهل الجلسة مفتوحة الآن (مراجعة 23/09/2026) — غائبان عند خادمٍ أقدم */
  lastCheckInAt?: string | null; openSession?: boolean;
  unmatchedCheckIns: number; unmatchedCheckOuts: number;
  isAbsent: boolean; forgotCheckIn: boolean; forgotCheckOut: boolean;
  pairs: EventPair[];
  unmatchedCheckInEventIds: string[]; unmatchedCheckOutEventIds: string[];
  events: AttendanceEvent[];
  classification: DayClassificationInfo | null;
  anomalies: AttendanceAnomaly[];
  lateMinutes?: number;
}

export interface AttendanceToday extends AttendanceDay { schedule: AttendanceSchedule }
export interface AttendanceDaysResponse { page: ReviewPage; items: AttendanceDay[]; scheduleMissingDates: string[] }

/** جسم النقرة: بلا وقت وبلا معرّف موظف وبلا أي بيانات جهاز. الإحداثيات معًا أو لا شيء. */
export interface TapBody {
  locationMissing: boolean;
  gpsLat?: number; gpsLon?: number; gpsAccuracyM?: number;
  locationDeniedReason?: string;
  /** أندرويد علّم القراءة «مزيّفة» (برنامج تزوير موقع) — 2.1.2، المالك 03/10/2026: «أ» */
  mockLocation?: boolean;
  /** البصمة من تطبيق الموبايل (الإضافة الأصلية) لا من متصفّح */
  fromApp?: boolean;
}

export interface LeaveRequest {
  id: string; status: LeaveStatus; startDate: string; endDate: string; days: number; reason: string;
  noticeHours: number | null; noticeWarning: boolean;
  decidedAt: string | null; decisionReason: string | null; cancelledAt: string | null; createdAt: string;
}
export interface LeaveResponse { page: ReviewPage; items: LeaveRequest[] }

/** سحب التذكير: POST يُحتسب ظهورًا. النصّ يأتي من الخادم ولا تخترعه الواجهة. */
export interface ReminderPull {
  anchorDate: string; situation: 'NO_CHECK_IN_YET'; due: boolean;
  message?: string; reason?: 'SITUATION_NOT_ACTIVE' | 'DEBOUNCED' | 'LIMIT_REACHED';
  shownCount: number; remaining: number;
}

// ---------- الأدمن ----------
export interface AttendanceEmployee {
  userId: string; fullNameAr: string; sheetAlias: string | null;
  role: 'ADMIN' | 'SALES' | 'PARTNER'; isActive: boolean; enrolledAt: string; enrolledBy: string | null;
  schedule: (AttendanceSchedule & { dayCut: string }) | null;
}
export interface AttendanceEmployeesResponse { page: ReviewPage; items: AttendanceEmployee[]; today: string }

export interface AdminAttendanceDay extends AttendanceDay { userId: string; fullNameAr: string | null }
export interface AdminDaysResponse {
  page: ReviewPage; items: AdminAttendanceDay[]; scheduleMissing: { userId: string; dates: string[] }[];
}
export interface AdminUserDaysResponse { page: ReviewPage; items: AttendanceDay[]; scheduleMissingDates: string[] }

export interface AdminLeave extends LeaveRequest { userId: string; fullNameAr: string; decidedBy: string | null }
export interface AdminLeaveResponse { page: ReviewPage; pendingCount: number; items: AdminLeave[] }

export interface ClassificationResult {
  userId: string; anchorDate: string; classification: DayClassificationInfo;
  history: (DayClassificationInfo & { id: string })[];
}

export interface AttendanceCounters {
  days: number; presentDays: number; absentDays: number; onLeaveDays: number;
  forgotCheckInDays: number; forgotCheckOutDays: number;
  completeDays: number; shortDays: number; undeterminedDays: number;
  provisionalDays: number; classifiedDays: number;
  totalWorkedMinutes: number; totalExpectedMinutes: number; scheduleMissingDays: number;
  anomalies: Record<AttendanceAnomaly, number>;
  lateDays?: number; totalLateMinutes?: number;
}
export interface AttendanceReport {
  range: { from: string; to: string }; employees: number;
  perEmployee: { userId: string; fullNameAr: string | null; counters: AttendanceCounters }[];
  totals: AttendanceCounters;
}

export interface IntegrityFinding { userId: string; fullNameAr: string; detail: string }
export interface AttendanceIntegrity {
  range: { from: string; to: string };
  anchorDateDrift: IntegrityFinding[]; exceptionCutDrift: IntegrityFinding[];
  scheduleCoverageGaps: IntegrityFinding[]; enrolledWithoutTodaySchedule: IntegrityFinding[];
  leaveOverClassifiedDays: IntegrityFinding[]; classifiedDaysWithEvents: IntegrityFinding[];
}

export interface ScheduleVersion {
  id: string; effectiveFrom: string; effectiveTo: string | null;
  kind: 'FIXED' | 'FLEXIBLE'; startLocal: string | null; endLocal: string | null; crossesMidnight: boolean;
  expectedMinutes: number; graceMinutes: number | null; workdays: number[];
  note: string | null; createdAt: string; createdBy: string | null; dayCut: string;
}
export interface ScheduleException {
  id: string; exceptionDate: string; kind: 'FIXED' | 'FLEXIBLE';
  startLocal: string | null; endLocal: string | null; crossesMidnight: boolean;
  expectedMinutes: number; graceMinutes: number | null; reason: string;
  live: boolean; supersededBy: string | null; createdAt: string; createdBy: string | null; dayCut: string;
}
export interface ScheduleResponse { userId: string; versions: ScheduleVersion[]; exceptions: ScheduleException[] }

/** شكل الجدول المرسَل — نفس التقسيم المعتمد: المرن يرفض الوردية والسماح لا يتجاهلهما. */
export type ScheduleShapeBody =
  | { kind: 'FIXED'; startLocal: string; endLocal: string; expectedMinutes: number; graceMinutes: number; workdays?: number[]; note?: string | null }
  | { kind: 'FLEXIBLE'; expectedMinutes: number; workdays?: number[]; note?: string | null };
/**
 * عمولة البياع لشهر: settlement = null يعني أن الشهر لم يُحسب بعد (ليست حالة خطأ).
 * ومعها لوحة الشهر (Step 6B) — كلها محسوبة في الخادم من السياسة السارية ونسخة القواعد:
 *   · newTarget — حدّ التارجت، لا ثابت في الواجهة.
 *   · bonusTier — شريحة البونص كما تعرّفها القاعدة (grants_bonus).
 *   · remainingToBonus — الباقي للوصول إليها (التفسير المعتمد أ)، وnull لخطة بلا بونص.
 *   · counts — عدّ الشهر الحيّ بنوع العملية.
 *   · targetApplies — أهلية الخطة للشرائح وللبونص، من نسخة القواعد لا من الدور.
 */
export interface MyCommission {
  month: string; settlement: Settlement | null; lines: SettlementLine[];
  newTarget: number | null;
  bonusTier: { code: string; newMin: number } | null;
  remainingToBonus: number | null;
  counts: OperationCounts;
  targetApplies: { reduction: boolean; bonus: boolean } | null;
}
export interface CalculateResult { settlement: Settlement; linesCount: number; openReviewCases: number }

/** نتيجة بياع واحد داخل «تسوية جميع البياعين» — تُعرض كما هي ولا يُخفى منها فشل. */
export interface BulkSettlementRow {
  userId: string; sellerName: string;
  outcome: 'DONE' | 'SKIPPED' | 'FAILED';
  settlementId?: string; status?: string; linesCount?: number; reason?: string;
}
export interface BulkSettlementResult {
  month: string; done: number; skipped: number; failed: number; results: BulkSettlementRow[];
}

/** نتيجة شهر Top Achiever كما يراها الأدمن — كل رقم مجمَّع على الخادم لا محسوبًا هنا. */
export interface AchieverRank {
  userId: string; fullNameAr: string; totalOps: number;
  opsNew: number; opsRepeat: number; opsModification: number; opsCancellation: number; opsCompany: number;
}
export interface AchieverMonthRow {
  id: string; month: string; userId: string; employeeName: string; message: string | null;
  source: 'AUTO' | 'MANUAL'; hasPhoto: boolean;
  totalOps: number | null; opsNew: number | null; opsRepeat: number | null;
  opsModification: number | null; opsCancellation: number | null; opsCompany: number | null;
  createdByName?: string | null;
}
export interface AchieverAdminView {
  month: string;
  current: AchieverMonthRow | null;
  ranking: AchieverRank[];
  history: AchieverMonthRow[];
}

// ---------- أعياد ميلاد العملاء (0085) — أساس حملة التهنئة ----------
export interface AnalyticsBirthdayRow {
  customerName: string; phone: string; dateOfBirth: string;
  /** يوم الميلاد في السنة الجارية — به يُعرف «اليوم» من «قادم في الشهر». */
  day: string;
  turningAge: number; bookings: number; salesName: string | null;
}
export interface AnalyticsBirthdays {
  today: AnalyticsBirthdayRow[];
  thisMonth: AnalyticsBirthdayRow[];
  /** عملاءُ بلا تاريخ ميلاد — ما لم يُجمع لا يُهنَّأ. */
  missingCount: number;
  customersTotal: number;
}

// ---------- مراجعة «تصفية حساب الحجز» (0167) ----------
export type SettlementReviewStatus = 'NOT_REVIEWED' | 'REVIEWED' | 'NEEDS_RECHECK';
export interface SettlementReview { id: string; note: string; reviewedAt: string; reviewedByName: string | null }

/**
 * **كود نموذج الحجز** (0179 — المراجعة الشاملة الثالثة، والمالك 03/10/2026: «موافق»): يُولد مرّةً عند فتح النموذج ويُرسل
 * مع كل ضغطة «حفظ» منه (ترويسة Idempotency-Key). فإن قطع النت بعد أن كتب الخادم الحجز ولم يصل ردّه، فالضغطة الثانية
 * تأخذ الحجز نفسه — لا حجزًا ثانيًا ودفعةً ثانية لنفس العميل.
 */
export function newFormKey(): string {
  const c = globalThis.crypto;
  if (c?.randomUUID) return c.randomUUID();
  const b = c.getRandomValues(new Uint8Array(16));
  b[6] = (b[6] & 0x0f) | 0x40; b[8] = (b[8] & 0x3f) | 0x80;
  const h = [...b].map(x => x.toString(16).padStart(2, '0')).join('');
  return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
}
